<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from '../../../../components/Modal.vue';
import MessageTemplateForm from './MessageTemplateForm.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  funnels: {
    type: Array,
    default: () => [],
  },
  selectedFunnelId: {
    type: [String, Number],
    default: null,
  },
});

const { t } = useI18n();
const emit = defineEmits(['switch-view', 'template-created']);

const showNewTemplateModal = ref(false);

const defaultInitialData = computed(() => {
  if (!props.funnels.length) {
    return {};
  }

  const funnel =
    props.funnels.find(
      funnelItem => String(funnelItem.id) === String(props.selectedFunnelId)
    ) || props.funnels[0];

  const stages = Object.entries(funnel.stages || {}).map(([id, stage]) => ({
    id: String(id),
    name: stage.name,
    position: stage.position ?? 0,
  }));

  const firstStage = stages.sort((a, b) => a.position - b.position)[0];

  return {
    funnel_id: String(funnel.id),
    stage_id: firstStage?.id || '',
  };
});

const handleBack = () => {
  emit('switch-view', 'kanban');
};

const handleTemplateCreated = template => {
  showNewTemplateModal.value = false;
  emit('template-created', template);
};
</script>

<template>
  <header class="message-templates-header">
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-4">
        <Button variant="ghost" color="slate" size="sm" @click="handleBack">
          <svg
            class="h-4 w-4 shrink-0"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </Button>
        <h1 class="text-2xl font-medium">
          {{ t('KANBAN.MESSAGE_TEMPLATES.TITLE') }}
        </h1>
      </div>
      <Button variant="solid" color="blue" size="sm" @click="showNewTemplateModal = true">
        <svg
          class="mr-2 h-4 w-4 shrink-0"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M12 5v14M5 12h14" />
        </svg>
        {{ t('KANBAN.MESSAGE_TEMPLATES.ADD') }}
      </Button>
    </div>

    <Modal
      v-model:show="showNewTemplateModal"
      size="large"
      :on-close="() => (showNewTemplateModal = false)"
      class="template-modal"
    >
      <div
        class="max-h-[min(85vh,52rem)] overflow-y-auto px-3 py-4 sm:px-6 sm:py-5"
      >
        <MessageTemplateForm
          :funnels="funnels"
          :initial-data="defaultInitialData"
          @saved="handleTemplateCreated"
          @close="showNewTemplateModal = false"
        />
      </div>
    </Modal>
  </header>
</template>

<style lang="scss" scoped>
.message-templates-header {
  padding: var(--space-normal);
  border-bottom: 1px solid var(--color-border);
  background: var(--white);

  @apply dark:border-slate-800 dark:bg-slate-900;

  h1 {
    @apply dark:text-slate-100;
  }
}

:deep(.template-modal) {
  .modal-container {
    @apply w-[min(100vw-1rem,75rem)] max-w-[min(100vw-1rem,75rem)] dark:bg-slate-900;
  }
}
</style>
